import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, User, Loader2 } from "@/lib/icons";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/benefitness_icon-2.svg";
import { BrandLabel } from "@/components/BrandLabel";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const isReferred = searchParams.get("ref") === "1";
  const [isLogin, setIsLogin] = useState(!isReferred); // Default to signup when referred
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (forgotPassword) {
        const redirectBase = window.location.hostname === "smrtlyfit.app"
          ? "https://smrtlyfit.app"
          : window.location.origin;
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${redirectBase}/reset-password`,
        });
        if (error) throw error;
        toast({
          title: "Check your email",
          description: "We sent you a password reset link.",
        });
        setForgotPassword(false);
      } else if (isLogin) {
        const { data: loginData, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast({ title: "Welcome back!", description: "You're now logged in." });
        // Post-login: link referral if cookie exists
        if (loginData?.user) {
          try {
            const raw = localStorage.getItem("bf_referral");
            if (raw) {
              const ref = JSON.parse(raw);
              const clickedAt = new Date(ref.clickedAt);
              const daysSinceClick = (Date.now() - clickedAt.getTime()) / (1000 * 60 * 60 * 24);
              if (daysSinceClick <= 30 && ref.referralId) {
                await supabase.functions.invoke("referral-signup", {
                  body: { referralId: ref.referralId },
                });
                localStorage.removeItem("bf_referral");
              }
            }
          } catch { /* non-fatal */ }
        }
      } else {
        const signupRedirect = window.location.hostname === "smrtlyfit.app"
          ? "https://smrtlyfit.app"
          : window.location.origin;
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: signupRedirect,
            data: { display_name: displayName },
          },
        });
        if (error) throw error;
        toast({
          title: "Check your email",
          description: "We sent you a confirmation link to verify your account.",
        });
        // Note: referral-signup linkage happens after email confirmation + first login
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 pt-safe">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={logo} alt="smrtlyFit logo" className="h-12 w-12" />
          <h1 className="font-display text-3xl font-bold text-foreground"><BrandLabel /></h1>
        </div>

        {/* Card */}
        <div className="bg-card rounded-xl border shadow-sm p-8">
          <h2 className="font-display text-2xl font-bold text-center text-card-foreground mb-2">
            {forgotPassword ? "Reset password" : isLogin ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {forgotPassword
              ? "Enter your email and we'll send you a reset link"
              : isLogin
                ? "Log in to track your fitness journey"
                : "Sign up to start tracking your macros"}
          </p>
          {isReferred && !isLogin && !forgotPassword && (
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                🎉 Referred by a friend
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && !forgotPassword && (
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Your name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {!forgotPassword && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setForgotPassword(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {forgotPassword ? "Send reset link" : isLogin ? "Log in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            {forgotPassword ? (
              <button
                type="button"
                onClick={() => setForgotPassword(false)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="font-semibold text-primary">Back to login</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <span className="font-semibold text-primary">
                  {isLogin ? "Sign up" : "Log in"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
